import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { LeadTimeReport } from '../types';

/**
 * Koliko prođe od prodaje do isporuke.
 *
 * ZAŠTO JE OVO PRVI BROJ NA EKRANU
 *   Po CLAUDE.md je brzina prednost firme nad konkurencijom. Sve ostalo u
 *   analitici meri IZVRŠENJE ture — koliko je stajanje trajalo, da li je tura
 *   upala u smenu. Nijedan od tih brojeva ne kaže koliko kupac čeka.
 *
 * Prikazuje se MEDIJANA, ne prosek: jedna isporuka koja je čekala mesec dana
 * pomeri prosek, a o uobičajenom danu ne govori ništa.
 */
export default function LeadTime({ days }: { days: number }) {
  const [report, setReport] = useState<LeadTimeReport | null>(null);
  const [greska, setGreska] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setReport(await api.leadTime(days));
      setGreska(null);
    } catch (e) {
      setReport(null);
      setGreska(e instanceof Error ? e.message : String(e));
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  if (greska) {
    return (
      <section className="panel">
        <h2>Od prodaje do isporuke</h2>
        <div className="health health--bad">
          Brzina se nije mogla izračunati: {greska}. Dan prodaje zna ERP, pa bez
          njega ovog broja nema — <strong>ne prikazuje se procena</strong>.
        </div>
      </section>
    );
  }

  if (!report) {
    return (
      <section className="panel">
        <h2>Od prodaje do isporuke</h2>
        <p className="muted">Učitavam…</p>
      </section>
    );
  }

  const { current, previous } = report;
  const najvise = Math.max(1, ...report.distribution.map((b) => b.deliveries));

  return (
    <section className="panel">
      <h2>Od prodaje do isporuke</h2>

      <p className="note">
        Koliko kupac čeka od dana kupovine do dana kad mu roba stigne. Ovo je
        jedini broj koji meri <strong>brzinu</strong>; sve ostalo na ovoj kartici
        meri izvršenje ture.
      </p>

      {current.median === null ? (
        <p className="muted">
          U poslednjih {days} dana ima {current.deliveries}{' '}
          {current.deliveries === 1 ? 'isporuku' : 'isporuka'} — premalo da bi
          presek nešto značio.
        </p>
      ) : (
        <>
          <p className="brzina__broj">
            <strong>{current.median}</strong>{' '}
            {current.median === 1 ? 'dan' : 'dana'}
            <span className="muted"> · medijana, {current.deliveries} isporuka</span>
          </p>

          <p>
            {current.withinThree}% stigne za <strong>tri dana ili manje</strong>.
            {previous.median !== null && (
              <>
                {' '}Prethodnih {days} dana:{' '}
                <strong>{previous.median}</strong>{' '}
                {previous.median === 1 ? 'dan' : 'dana'}{' '}
                <span className="muted">({previous.deliveries} isporuka)</span>
                {previous.median !== current.median && (
                  <span className={current.median < previous.median ? 'flag flag--ok' : 'flag flag--warn'}>
                    {current.median < previous.median ? 'brže' : 'sporije'}
                  </span>
                )}
              </>
            )}
          </p>

          <ul className="brzina__raspodela">
            {report.distribution.map((b) => (
              <li key={b.days}>
                <span className="brzina__oznaka">{b.label}</span>
                <span className="brzina__traka">
                  <span style={{ width: `${Math.round(100 * b.deliveries / najvise)}%` }} />
                </span>
                <span className="brzina__broj-male">{b.deliveries}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {report.unknown > 0 && (
        <p className="note">
          Za {report.unknown}{' '}
          {report.unknown === 1 ? 'isporuku' : 'isporuka'} se dan prodaje nije
          mogao pročitati — skoro uvek otpremnica iz prethodne poslovne godine,
          koju povezana baza ne vidi. Te nisu ušle u račun.
        </p>
      )}
    </section>
  );
}
